'use client';

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import useSWR from 'swr';
import {
  type VfxMode,
  type VfxTier,
  modeToTier,
  modeToTierAsync,
  applyVfxTier,
  detectDeviceTier,
} from '@/utils/vfxManager';

// Комментарий на русском: контекст для управления VFX из любого места приложения.
type VfxContextValue = {
  mode: VfxMode;
  tier: VfxTier;
  isLoading: boolean;
  isAutoDetecting: boolean;
};

const VfxContext = createContext<VfxContextValue>({
  mode: 'auto',
  tier: 'medium',
  isLoading: true,
  isAutoDetecting: false,
});

export function useVfx() {
  return useContext(VfxContext);
}

type Props = {
  children: ReactNode;
};

export default function VfxProvider({ children }: Props) {
  // Комментарий на русском: загружаем настройки сайта с revalidate каждые 60 секунд.
  const { data, isLoading } = useSWR<{ visual_effects_mode?: VfxMode }>(
    // Тот же ключ, что обновляет админка: выключение VFX сразу останавливает эффекты.
    '/api/settings/',
    {
      refreshInterval: 60_000, // 60 сек
      revalidateOnFocus: false,
      dedupingInterval: 30_000,
    }
  );

  const mode: VfxMode = data?.visual_effects_mode || 'auto';
  const [tier, setTier] = useState<VfxTier>(() => {
    // Комментарий на русском: начальное значение — синхронное определение (из кэша или эвристик).
    if (typeof window !== 'undefined') {
      return detectDeviceTier();
    }
    return 'medium';
  });
  const [isAutoDetecting, setIsAutoDetecting] = useState(false);

  // Комментарий на русском: применяем tier при изменении mode.
  useEffect(() => {
    let cancelled = false;

    const applyMode = async () => {
      if (mode === 'auto') {
        // Комментарий на русском: режим auto — запускаем асинхронное определение с FPS-тестом.
        setIsAutoDetecting(true);

        // Сначала применяем синхронный результат (из кэша или эвристик)
        const quickTier = modeToTier(mode);
        if (!cancelled) {
          setTier(quickTier);
          applyVfxTier(quickTier);
        }

        // Затем запускаем полный FPS-тест
        try {
          const asyncTier = await modeToTierAsync(mode);
          if (!cancelled) {
            setTier(asyncTier);
            applyVfxTier(asyncTier);
          }
        } catch {
          // Ошибка FPS-теста — оставляем синхронный результат
        } finally {
          if (!cancelled) {
            setIsAutoDetecting(false);
          }
        }
      } else {
        // Комментарий на русском: принудительный режим — применяем сразу.
        const newTier = modeToTier(mode);
        if (!cancelled) {
          setTier(newTier);
          applyVfxTier(newTier);
          setIsAutoDetecting(false);
        }
      }
    };

    void applyMode();

    return () => {
      cancelled = true;
    };
  }, [mode]);

  // Комментарий на русском: устанавливаем безопасный дефолт до загрузки настроек.
  useEffect(() => {
    if (isLoading && typeof document !== 'undefined') {
      // До загрузки применяем синхронное определение
      const quickTier = detectDeviceTier();
      applyVfxTier(quickTier);
    }
  }, [isLoading]);

  return (
    <VfxContext.Provider value={{ mode, tier, isLoading, isAutoDetecting }}>
      {children}
    </VfxContext.Provider>
  );
}
